import { getApiEndpoint } from "../config/config";

// A small in-memory cache of rep-profile reads (GET /v1/rep/details/:rep_id),
// keyed by rep_id, so the details drawer can open instantly when the row was
// hovered (prefetch) or the rep was opened recently. Entries expire quickly —
// this smooths navigation, it is not a source of truth: any save refetches.
const TTL_MS = 60 * 1000;

const cache = new Map(); // rep_id -> { data, at }
const inflight = new Map(); // rep_id -> Promise (dedupes concurrent prefetches)

export function getCachedRepDetails(repId) {
  const entry = cache.get(String(repId));
  if (!entry) return null;
  if (Date.now() - entry.at > TTL_MS) {
    cache.delete(String(repId));
    return null;
  }
  return entry.data;
}

export function setCachedRepDetails(repId, data) {
  if (!data) return;
  cache.set(String(repId), { data, at: Date.now() });
}

export function clearCachedRepDetails(repId) {
  cache.delete(String(repId));
}

// Warms the cache for a rep (e.g. on row hover) without blocking anything.
// `getJwt` is the auth context's getVerifiedJWT. Silent on failure — the
// drawer just falls back to its normal fetch.
export function prefetchRepDetails(repId, getJwt) {
  const key = String(repId);
  if (!key || getCachedRepDetails(key) || inflight.has(key)) return;

  const run = (async () => {
    try {
      const jwt = await getJwt();
      if (!jwt) return;
      const response = await fetch(getApiEndpoint(`/v1/rep/details/${key}`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });
      if (!response.ok) return;
      const result = await response.json();
      if (result?.data) setCachedRepDetails(key, result.data);
    } catch (err) {
      // Prefetch is best-effort.
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, run);
}
